import React from "react";
import {
  Container,
  Skeleton,
  Box,
  Stack,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CommonBackground from "../../CommonBackground";

/**
 * Shimmer loading component for ChapterDetailsPage
 * Matches the structure of ChapterHero, ChapterContent, ChapterNavigation, CommentSection, and TableOfContents
 */
const ChapterDetailsPageShimmer = () => {
  return (
    <CommonBackground>
      {/* Hero Section Shimmer */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          pt: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
          {/* Mobile Layout Shimmer */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Back to Story Link - Centered */}
            <Box sx={{ width: "100%", textAlign: "center", mb: 1.5 }}>
              <Skeleton variant="text" width="60%" height={24} sx={{ mx: "auto" }} />
            </Box>

            {/* Chapter Number Badge and Comment Count - Centered */}
            <Box sx={{ width: "100%", textAlign: "center", mb: 2 }}>
              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ gap: 1.5 }}>
                <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={120} height={36.5} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>

            {/* Chapter Title - Centered */}
            <Box sx={{ width: "100%", textAlign: "center", mb: 1.5 }}>
              <Skeleton variant="text" width="85%" height={36} sx={{ mx: "auto", mb: 1 }} />
            </Box>

            {/* Publication Date - Centered */}
            <Box sx={{ width: "100%", textAlign: "center", mb: 2.5 }}>
              <Skeleton variant="text" width="50%" height={20} sx={{ mx: "auto" }} />
            </Box>

            {/* Stats Row - Centered */}
            <Box sx={{ width: "100%", mb: 2.5, display: "flex", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" sx={{ gap: 1.5 }}>
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 999 }} />
                <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>

            {/* Action Buttons - Centered */}
            <Box sx={{ width: "100%", mb: 2.5, display: "flex", justifyContent: "center" }}>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" sx={{ gap: 1.5 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="circular" width={40} height={40} />
              </Stack>
            </Box>
          </Box>

          {/* Desktop/Tablet Layout Shimmer */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "relative",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {/* Hero Container */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minHeight: { xs: "40vh", sm: "50vh", md: "55vh" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)"
                    : "linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)",
                borderRadius: 3,
                mb: 4,
                overflow: "hidden",
              }}
            >
              {/* Cover Image Background */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.3,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{ borderRadius: 3 }}
                />
              </Box>

              {/* Content Container */}
              <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: 6 }}>
                <Box
                  sx={{
                    maxWidth: { xs: "100%", lg: 1200 },
                    mx: "auto",
                    px: { xs: 2, sm: 3, md: 4 },
                  }}
                >
                  {/* Back to Story Link */}
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="text" width={200} height={24} />
                  </Box>

                  {/* Chapter Number Badge */}
                  <Box sx={{ mb: 2 }}>
                    <Skeleton variant="rounded" width={180} height={32} sx={{ borderRadius: 999 }} />
                  </Box>

                  {/* Chapter Title */}
                  <Box sx={{ mb: 1 }}>
                    <Skeleton variant="text" width="70%" height={48} />
                  </Box>

                  {/* Stats Row */}
                  <Box sx={{ pt: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
                      <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={140} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 999 }} />
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ mt: 3 }}>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                      <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={140} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 999 }} />
                    </Stack>
                  </Box>
                </Box>
              </Container>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Divider Shimmer */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 } }}>
        <Divider
          sx={{
            my: { xs: 3, md: 4 },
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
          }}
        />
      </Container>

      {/* Main Content Layout Shimmer */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4, lg: 6 }, pb: { xs: 4, md: 6 }, pt: { lg: 0 } }}>
        <Box
          sx={{
            maxWidth: { xs: "100%", lg: 1400 },
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, md: 5 },
            alignItems: "flex-start",
          }}
        >
          {/* Main Content - Wide */}
          <Box sx={{ flex: { xs: "1 1 auto", lg: "1 1 65%" }, minWidth: 0, width: "100%" }}>
            {/* Chapter Content Shimmer */}
            <Paper
              elevation={0}
              sx={{
                mb: { xs: 4, md: 5 },
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: { xs: 2, md: 3 },
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Stack spacing={2}>
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="95%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="90%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="98%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="92%" height={24} />
                <Skeleton variant="text" width="100%" height={24} />
              </Stack>
            </Paper>

            {/* Chapter Navigation Shimmer */}
            <Box sx={{ mb: { xs: 4, md: 5 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3, md: 4 },
                  borderRadius: { xs: 2, md: 3 },
                  backgroundColor: "transparent",
                  backdropFilter: "blur(12px)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 2px 8px rgba(0,0,0,0.1)"
                      : "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={48}
                  sx={{ borderRadius: 2 }}
                />
              </Paper>
            </Box>

            {/* Comments Section Shimmer */}
            <Box sx={{ mb: { xs: 4, md: 5 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3, md: 4 },
                  borderRadius: { xs: 2, md: 3 },
                  backgroundColor: "transparent",
                  backdropFilter: "blur(12px)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)"
                    }`,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 2px 8px rgba(0,0,0,0.1)"
                      : "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Skeleton variant="text" width="40%" height={28} sx={{ mb: 3 }} />
                <Box sx={{ mb: 3 }}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={100}
                    sx={{ borderRadius: 2, mb: 2 }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={120}
                    height={40}
                    sx={{ borderRadius: 999 }}
                  />
                </Box>
                <Stack spacing={2}>
                  {[...Array(3)].map((_, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Box>

          {/* Right Sidebar: Table of Contents Shimmer - Only render on desktop */}
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              width: 280,
              flexShrink: 0,
              position: "sticky",
              top: 100,
              alignSelf: "flex-start",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: "transparent",
                backdropFilter: "blur(12px)",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)"
                  }`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2.5 }} />
              <Stack spacing={1.5}>
                {[...Array(5)].map((_, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Skeleton variant="circular" width={8} height={8} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Container>
    </CommonBackground>
  );
};

export default ChapterDetailsPageShimmer;

